import { useEffect, useRef, useState } from 'react';
import SwitchInput from '../../components/SwitchInput/SwitchInput';
import { savePlan } from '../../services/PlansService';

// Secondary caps, edited as a compact grid. All default to -1 (unlimited).
const FEATURE_LIMIT_INPUTS = [
    { id: 'maxIndexes', label: 'Max. Indexes:' },
    { id: 'maxGridLevels', label: 'Max. Grid Levels:' },
    { id: 'maxActions', label: 'Max. Actions:' },
    { id: 'maxBacktestDays', label: 'Max. Backtest Days:' },
    { id: 'maxBacktestCandles', label: 'Max. Backtest Candles:' },
    { id: 'maxNotificationsPerHour', label: 'Max. Notif./hour:' },
    { id: 'maxWsConnections', label: 'Max. WS conns. (per user):' },
];

/**
 * props:
 * - data
 * - onSubmit
 */
function PlanModal(props) {
    const DEFAULT_PLAN = {
        code: '',
        name: '',
        maxAutomations: 0,
        maxMonitors: 0,
        maxBacktests: 0,
        maxIndexes: -1,
        maxGridLevels: -1,
        maxActions: -1,
        maxBacktestDays: -1,
        maxBacktestCandles: -1,
        maxNotificationsPerHour: -1,
        maxWsConnections: -1,
        hasFutures: false,
        hasLaunch: false,
        hasStrategies: false,
        isTrial: false,
        isActive: false,
        priceMonthly: '',
        stripePriceId: '',
        stripePaymentLink: '',
    };

    const [error, setError] = useState('');

    const [plan, setPlan] = useState(DEFAULT_PLAN);

    const btnClose = useRef('');

    function onSubmit(event) {
        const code = Number(plan.code);
        if (plan.code === '' || !Number.isInteger(code) || code < 1) {
            setError('Code must be a positive integer');
            return;
        }

        if (!plan.name?.trim()) {
            setError('Name is required');
            return;
        }

        savePlan(plan.id, plan)
            .then(result => {
                btnClose.current.click();
                if (props.onSubmit) props.onSubmit(result);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setError(err.response ? err.response.data : err.message);
            });
    }

    function onInputChange(event) {
        setPlan(prevState => ({
            ...prevState,
            [event.target.id]: event.target.value,
        }));
        setError('');
    }

    useEffect(() => {
        setPlan(props.data);
        setError('');
    }, [props.data.id]);

    // The reset on close leaves the form empty while props.data keeps the same
    // id, so the effect above does not run when the same plan is reopened.
    const dataRef = useRef(props.data);
    dataRef.current = props.data;

    useEffect(() => {
        const modal = document.getElementById('modalPlan');
        modal.addEventListener('hidden.bs.modal', event => {
            setPlan({ ...DEFAULT_PLAN });
        });
        modal.addEventListener('show.bs.modal', event => {
            setPlan(dataRef.current);
            setError('');
        });
    }, []);

    return (
        <div
            className="modal fade"
            id="modalPlan"
            tabIndex="-1"
            role="dialog"
            aria-labelledby="modalTitleNotify"
            aria-hidden="true"
        >
            <div
                className="modal-dialog modal-dialog-centered modal-xl"
                role="document"
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title" id="modalTitleNotify">
                            {props.data.id ? 'Edit ' : 'New '}Plan
                        </p>
                        <button
                            ref={btnClose}
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="close"
                        ></button>
                    </div>
                    <div className="modal-body">
                        <div className="form-group">
                            <div className="row">
                                <div className="col-4 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="code">
                                            Code:{' '}
                                            <span className="required">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            id="code"
                                            className="form-control"
                                            onChange={onInputChange}
                                            value={plan.code ?? ''}
                                            placeholder="100"
                                            min="1"
                                            step="1"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="col-8 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="name">
                                            Name:{' '}
                                            <span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            className="form-control"
                                            onChange={onInputChange}
                                            value={plan.name || ''}
                                            placeholder="Plan's name"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-4 col-sm-6 mb-3">
                                    <div className="form-group mb-4">
                                        <label htmlFor="maxAutomations">
                                            Max. Automations:
                                        </label>
                                        <input
                                            type="number"
                                            id="maxAutomations"
                                            className="form-control"
                                            onChange={onInputChange}
                                            value={plan.maxAutomations || ''}
                                            placeholder="0"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4 col-sm-6 mb-3">
                                    <div className="form-group mb-4">
                                        <label htmlFor="maxMonitors">
                                            Max. Monitors:
                                        </label>
                                        <input
                                            type="number"
                                            id="maxMonitors"
                                            className="form-control"
                                            onChange={onInputChange}
                                            value={plan.maxMonitors || ''}
                                            placeholder="0"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4 col-sm-6 mb-3">
                                    <div className="form-group mb-4">
                                        <label htmlFor="maxBacktests">
                                            Max. Backtests/mo.:
                                        </label>
                                        <input
                                            type="number"
                                            id="maxBacktests"
                                            className="form-control"
                                            onChange={onInputChange}
                                            value={plan.maxBacktests || ''}
                                            placeholder="0"
                                        />
                                    </div>
                                </div>
                            </div>
                            <p className="small text-muted">
                                Use -1 for unlimited.
                            </p>
                            <div className="row">
                                {FEATURE_LIMIT_INPUTS.map(field => (
                                    <div
                                        className="col-md-4 col-sm-6 mb-3"
                                        key={field.id}
                                    >
                                        <div className="form-group mb-4">
                                            <label htmlFor={field.id}>
                                                {field.label}
                                            </label>
                                            <input
                                                type="number"
                                                id={field.id}
                                                className="form-control"
                                                onChange={onInputChange}
                                                value={plan[field.id] ?? ''}
                                                placeholder="-1"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="row">
                                <div className="col-md-4 mb-3">
                                    <div className="form-group">
                                        <SwitchInput
                                            id="isActive"
                                            text="Is Active?"
                                            onChange={onInputChange}
                                            isChecked={plan.isActive || false}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <div className="form-group">
                                        <SwitchInput
                                            id="hasFutures"
                                            text="Has Futures?"
                                            onChange={onInputChange}
                                            isChecked={plan.hasFutures || false}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <div className="form-group">
                                        <SwitchInput
                                            id="hasLaunch"
                                            text="Has Launch?"
                                            onChange={onInputChange}
                                            isChecked={plan.hasLaunch || false}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-4 mb-3">
                                    <div className="form-group">
                                        <SwitchInput
                                            id="hasStrategies"
                                            text="Has Strategies?"
                                            onChange={onInputChange}
                                            isChecked={
                                                plan.hasStrategies || false
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <div className="form-group">
                                        <SwitchInput
                                            id="isTrial"
                                            text="Is Trial?"
                                            onChange={onInputChange}
                                            isChecked={plan.isTrial || false}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-4 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="priceMonthly">
                                            Price/mo. (USD):
                                        </label>
                                        <input
                                            type="number"
                                            id="priceMonthly"
                                            className="form-control"
                                            onChange={onInputChange}
                                            value={plan.priceMonthly ?? ''}
                                            placeholder="0.00"
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-8 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="stripePriceId">
                                            Stripe Price ID:
                                        </label>
                                        <input
                                            type="text"
                                            id="stripePriceId"
                                            className="form-control"
                                            onChange={onInputChange}
                                            value={plan.stripePriceId || ''}
                                            placeholder="price_..."
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="stripePaymentLink">
                                            Stripe Payment Link:
                                        </label>
                                        <input
                                            type="url"
                                            id="stripePaymentLink"
                                            className="form-control"
                                            onChange={onInputChange}
                                            value={plan.stripePaymentLink || ''}
                                            placeholder="https://buy.stripe.com/..."
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        {error ? (
                            <div className="alert alert-danger mt-1 col-9 py-1">
                                {error}
                            </div>
                        ) : (
                            <></>
                        )}
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={onSubmit}
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PlanModal;
